import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Safe column list for the profiles base table.
 *
 * The backend revokes blanket SELECT on `public.profiles` and only grants
 * column-level SELECT for this subset to the `authenticated` role. Using
 * `select("*")` here would request the restricted `email` column and trigger
 * `permission denied for table profiles`, which in turn breaks the avatar,
 * name, bio and joined-date rendering across the app.
 *
 * The user's email is read from the auth session instead.
 */
export const PROFILE_SAFE_COLUMNS =
  "id, full_name, avatar_url, bio, created_at, updated_at, preferred_language, welcome_shown, onboarding_completed_at, default_landing, email_verified_at, verification_method, initial_verification_complete";

/**
 * Centralized profile data fetching hook.
 *
 * Profiles drive the avatar shown in the top header, the profile page, and
 * many other surfaces. We deliberately keep the cache short and refetch on
 * mount/focus so a saved photo or name change appears across the app within
 * a second — without forcing every consumer to invalidate manually.
 */
export function useProfile(userId: string | undefined) {
  return useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from("profiles")
        .select(PROFILE_SAFE_COLUMNS)
        .eq("id", userId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
    staleTime: 30 * 1000, // 30s — small payload, fast freshness wins
    gcTime: 5 * 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}
